import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  OutlinedInput,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Cancel';
import { useIntl } from 'react-intl';
import { useSendMessage } from '../../hooks/firebase/useSendMessage';
import { useExistChannel } from '../../hooks/store/useChannelHooks';
import { useSendFiles } from '../../hooks/firebase/useSendFiles';
import {
  InputError,
  isFileUploadEnabledForChannel,
  supportedMediaTypesString,
  validateMessageInput,
} from './helper';
import { ErrorMessage } from '../message-items/by-type/ErrorMessage';

type MessageInputProps = {
  width?: number;
  height?: number;
  channelGuid: string;
  standalone?: boolean;
};

type MessageInputMode = 'text' | 'file';

export const MessageInput = (props: MessageInputProps) => {
  const { channelGuid, standalone } = props;
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const channel = useExistChannel(channelGuid);
  const sendTextMessage = useSendMessage(channelGuid);
  const sendFiles = useSendFiles(channelGuid);
  const [text, setText] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [inputError, setInputError] = useState<InputError | null>(null);

  const isFileUploadEnabled = isFileUploadEnabledForChannel(channel);
  const messageInputMode: MessageInputMode = files.length ? 'file' : 'text';

  const onTextChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const textInput = event.target.value;
      setText(textInput ? textInput.trim() : null);
    },
    [],
  );

  const handleSelectFiles = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newFiles = Array.from(event.target.files ?? []);
      const clonedFiles = [...files];
      for (const newFile of newFiles) {
        if (files.every((file) => file.name !== newFile.name)) {
          clonedFiles.push(newFile);
        }
      }
      setFiles(clonedFiles);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [files],
  );

  useEffect(() => {
    validateMessageInput(text, files).then((inputError) =>
      setInputError(inputError),
    );
  }, [text, files]);

  const sendMessage = () => {
    if (inputError) {
      return;
    }
    if (files && files.length) {
      sendFiles(files);
      setFiles([]);
      return;
    } else if (text) {
      sendTextMessage(uuid(), text);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      setText(null);
    }
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendMessage();
  };

  const submitOnEnter = (
    event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    // For non-ASCII composing letters, the key event fires twice (keycode 13 and 229)
    // with the same key 'Enter'
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const isContentAvailable = () => {
    return (files && files.length) || text;
  };

  const removeFile = (index: number) => () => {
    const clonedFiles = [...files];
    clonedFiles.splice(index, 1);
    setFiles(clonedFiles);
  };

  if (channel.readonly) {
    return null;
  }

  return (
    <Box sx={{ px: standalone ? 2.5 : 5, pb: standalone ? 2.5 : 5 }}>
      <ErrorMessage messageId={inputError?.messageId} sx={{ mb: 2, ml: 6 }} />
      <FormControl
        component='form'
        fullWidth
        onSubmit={onSubmit}
        sx={{ width: props.width }}
      >
        <OutlinedInput
          placeholder={intl.formatMessage({ id: 'messages.input-placeholder' })}
          fullWidth
          multiline
          maxRows={5}
          disabled={messageInputMode === 'file'}
          inputRef={inputRef}
          onChange={onTextChange}
          onKeyDown={submitOnEnter}
          startAdornment={
            isFileUploadEnabled && (
              <>
                <IconButton
                  component='label'
                  disabled={!!text}
                  sx={{ position: 'absolute', top: 10 }}
                >
                  <AttachFileIcon />
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept={supportedMediaTypesString}
                    hidden
                    multiple
                    onChange={handleSelectFiles}
                  />
                </IconButton>
                <Box
                  className='hide-scrollbar'
                  sx={{
                    ml: 10,
                    mr: files.length ? 10 : 0,
                    display: 'flex',
                    flexWrap: 'wrap',
                    maxHeight: 150,
                    overflow: 'auto',
                  }}
                >
                  {files.map((file, index) => (
                    <Chip
                      key={file.name}
                      sx={{
                        p: 1,
                        m: 1,
                        maxWidth: 250,
                        overflow: 'hidden',
                        border:
                          inputError?.files && inputError.files.includes(file)
                            ? 'solid 0.5px #d00070'
                            : 'solid 0.5px #d8dade',
                        bgcolor: '#ecf2fb',
                        '& .MuiChip-deleteIcon': { color: '#898f9b' },
                      }}
                      label={file.name}
                      deleteIcon={<DeleteIcon />}
                      onDelete={removeFile(index)}
                    />
                  ))}
                </Box>
              </>
            )
          }
          endAdornment={
            <InputAdornment
              position='end'
              sx={{ position: 'absolute', right: 12, top: 30 }}
            >
              <IconButton
                type='submit'
                disabled={!isContentAvailable() || !!inputError}
              >
                <SendIcon />
              </IconButton>
            </InputAdornment>
          }
          sx={{
            '&.MuiOutlinedInput-root': {
              py: 2,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
            },
            '& .MuiOutlinedInput-input': {
              display: messageInputMode === 'file' ? 'none' : 'block',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: 7.5,
              border: inputError
                ? 'solid 0.5px #d00070 !important'
                : 'solid 0.5px #d8dade',
            },
            '& .MuiInputBase-inputMultiline': {
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            },
            alignItems: 'flex-start',
            minHeight: props.height ?? 60,
            overflow: 'auto',
            flexWrap: 'wrap',
          }}
        />
      </FormControl>
    </Box>
  );
};
